import { useState, useEffect } from 'react';
import {
    collection,
    onSnapshot,
    query,
    orderBy,
    where,
    addDoc,
    updateDoc,
    doc,
    Timestamp,
    getDocs,
    increment
} from 'firebase/firestore';
import { db } from '@/lib/firebase';

export interface ChatRoom {
    id: string;
    userId: string;
    userName: string;
    userEmail: string;
    adminId?: string;
    adminName?: string;
    status: 'active' | 'closed';
    lastMessage?: string;
    lastMessageTime?: Timestamp;
    unreadCountUser: number;
    unreadCountAdmin: number;
    createdAt: Timestamp;
    createdBy: 'admin' | 'user';
}

export const useChatRooms = (userRole?: 'admin' | 'user', userId?: string) => {
    const [chatRooms, setChatRooms] = useState<ChatRoom[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let q;

        if (userRole === 'user' && userId) {
            // User view: only their chat rooms
            q = query(
                collection(db, 'chatRooms'),
                where('userId', '==', userId),
                where('status', '==', 'active'),
                orderBy('lastMessageTime', 'desc')
            );
        } else if (userRole === 'admin') {
            // Admin view: all active chat rooms
            q = query(
                collection(db, 'chatRooms'),
                where('status', '==', 'active'),
                orderBy('lastMessageTime', 'desc')
            );
        } else {
            setLoading(false);
            return;
        }

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as ChatRoom[];
            setChatRooms(data);
            setLoading(false);
        }, (error) => {
            console.error('Error fetching chat rooms:', error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [userRole, userId]);

    const createChatRoom = async (
        targetUserId: string,
        targetUserName: string,
        targetUserEmail: string,
        createdBy: 'admin' | 'user',
        adminId?: string,
        adminName?: string
    ) => {
        try {
            // Check if chat room already exists
            const existingQuery = query(
                collection(db, 'chatRooms'),
                where('userId', '==', targetUserId),
                where('status', '==', 'active')
            );
            const existingRooms = await getDocs(existingQuery);

            if (!existingRooms.empty) {
                // Return existing room
                return {
                    success: true,
                    roomId: existingRooms.docs[0].id,
                    existing: true
                };
            }

            // Create new chat room
            const docRef = await addDoc(collection(db, 'chatRooms'), {
                userId: targetUserId,
                userName: targetUserName,
                userEmail: targetUserEmail,
                adminId: adminId || null,
                adminName: adminName || null,
                status: 'active',
                unreadCountUser: 0,
                unreadCountAdmin: 0,
                createdAt: Timestamp.now(),
                createdBy
            });

            return { success: true, roomId: docRef.id, existing: false };
        } catch (error) {
            console.error('Error creating chat room:', error);
            return { success: false, error };
        }
    };

    const closeChatRoom = async (roomId: string) => {
        try {
            await updateDoc(doc(db, 'chatRooms', roomId), {
                status: 'closed'
            });
            return { success: true };
        } catch (error) {
            console.error('Error closing chat room:', error);
            return { success: false, error };
        }
    };

    const markAsRead = async (roomId: string, userRole: 'admin' | 'user') => {
        try {
            const field = userRole === 'admin' ? 'unreadCountAdmin' : 'unreadCountUser';
            await updateDoc(doc(db, 'chatRooms', roomId), {
                [field]: 0
            });
            return { success: true };
        } catch (error) {
            console.error('Error marking as read:', error);
            return { success: false, error };
        }
    };

    return {
        chatRooms,
        loading,
        createChatRoom,
        closeChatRoom,
        markAsRead
    };
};
